import React, { useEffect, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * Two ways in:
 *
 *  - ?token=...  the link from a newsletter sent individually. The page looks
 *    the address up from the token and asks for one click - nothing to type.
 *  - no token    an email box, for newsletters sent by BCC, where one identical
 *    body goes to everyone and cannot carry a per-person link.
 *
 * The token path deliberately does NOT unsubscribe on page load. Mail clients
 * and security scanners follow links in messages to check them, which would
 * silently unsubscribe people who never clicked. Requiring a click means only
 * a person can trigger it.
 */
export default function Unsubscribe() {
  const [params] = useSearchParams();
  const token = params.get('token');

  const [state, setState] = useState(token ? 'loading' : 'form');
  const [address, setAddress] = useState('');
  const [email, setEmail] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    axios
      .get(`${API}/newsletter/subscription`, { params: { token } })
      .then(({ data }) => {
        if (cancelled) return;
        setAddress(data.email);
        setState(data.status === 'unsubscribed' ? 'already' : 'confirm');
      })
      .catch(() => !cancelled && setState('badlink'));
    return () => { cancelled = true; };
  }, [token]);

  const confirmWithToken = async () => {
    setBusy(true);
    try {
      await axios.post(`${API}/newsletter/unsubscribe`, { token });
      setState('done');
    } catch {
      setState('error');
    } finally {
      setBusy(false);
    }
  };

  const submitEmail = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setBusy(true);
    try {
      await axios.post(`${API}/newsletter/unsubscribe-by-email`, { email: email.trim() });
      setAddress(email.trim());
      setState('done');
    } catch (err) {
      setState(err.response?.status === 422 ? 'invalid' : 'error');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex min-h-[60vh] items-center justify-center px-4 py-20">
      <div className="w-full max-w-md rounded-lg border border-slate-200 bg-white p-8 shadow-sm">

        {state === 'loading' && (
          <p className="text-center text-slate-500">Checking your link…</p>
        )}

        {state === 'confirm' && (
          <div className="text-center">
            <h1 className="text-xl font-semibold text-slate-900">Unsubscribe?</h1>
            <p className="mt-3 text-sm text-slate-600">
              You'll stop receiving the Orboroi newsletter at
            </p>
            <p className="mt-2 break-all rounded-md bg-slate-50 px-3 py-2 font-medium text-slate-900">
              {address}
            </p>
            <button
              onClick={confirmWithToken}
              disabled={busy}
              className="mt-5 w-full rounded-md bg-[#379F7F] px-4 py-2 text-sm font-medium text-white hover:bg-[#2b7d64] disabled:opacity-50"
            >
              {busy ? 'Removing…' : 'Yes, unsubscribe me'}
            </button>
            <Link to="/" className="mt-3 block text-sm text-slate-500 hover:text-slate-700">
              No, keep me subscribed
            </Link>
          </div>
        )}

        {state === 'already' && (
          <div className="text-center">
            <h1 className="text-xl font-semibold text-slate-900">Already unsubscribed</h1>
            <p className="mt-3 text-sm text-slate-600">
              <span className="font-medium">{address}</span> is not on the newsletter list,
              so there's nothing to do.
            </p>
          </div>
        )}

        {state === 'done' && (
          <div className="text-center">
            <h1 className="text-xl font-semibold text-slate-900">You're unsubscribed</h1>
            <p className="mt-3 text-sm text-slate-600">
              {address ? <><span className="font-medium">{address}</span> has been removed
              from the Orboroi newsletter.</> : 'If that address was subscribed, it has been removed.'}
              {' '}Nothing else changes — if you've contacted us about work, we'll still reply.
            </p>
          </div>
        )}

        {(state === 'form' || state === 'invalid' || state === 'badlink') && (
          <>
            <h1 className="text-xl font-semibold text-slate-900">Unsubscribe</h1>
            <p className="mt-2 text-sm text-slate-600">
              {state === 'badlink'
                ? "That link isn't valid any more. Enter the address you subscribed with and we'll remove it."
                : "Enter the email address you subscribed with and we'll remove it from the Orboroi newsletter."}
            </p>
            <form onSubmit={submitEmail} className="mt-5 space-y-3">
              <input
                type="email"
                required
                value={email}
                onChange={(e) => { setEmail(e.target.value); if (state === 'invalid') setState('form'); }}
                placeholder="you@example.com"
                className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:border-[#379F7F] focus:outline-none focus:ring-1 focus:ring-[#379F7F]"
              />
              {state === 'invalid' && (
                <p className="text-sm text-red-600">That doesn't look like a valid email address.</p>
              )}
              <button
                type="submit"
                disabled={busy}
                className="w-full rounded-md bg-[#379F7F] px-4 py-2 text-sm font-medium text-white hover:bg-[#2b7d64] disabled:opacity-50"
              >
                {busy ? 'Removing…' : 'Unsubscribe'}
              </button>
            </form>
          </>
        )}

        {state === 'error' && (
          <div className="text-center">
            <h1 className="text-xl font-semibold text-slate-900">Something went wrong</h1>
            <p className="mt-3 text-sm text-slate-600">
              We couldn't process that just now. Please try again, or email{' '}
              <a href="mailto:contact@orboroi.com" className="text-[#379F7F] hover:underline">
                contact@orboroi.com
              </a>{' '}
              and we'll remove you manually.
            </p>
          </div>
        )}

        <div className="mt-6 text-center">
          <Link to="/" className="text-sm text-[#379F7F] hover:underline">
            Return to orboroi.com
          </Link>
        </div>
      </div>
    </div>
  );
}
